import React, { useContext } from 'react';
import { useDispatch } from 'react-redux';
import _ from 'lodash';
import { conf, isRelationshipVector, Attribute, Field, RelSide, RelPoint, RelType } from 'src/config';
import { Scope as ExplorerScope, ExplorerContext } from './Entity';
import * as types from '../types';
import * as actions from '../actions';
import { isPersistent, isTemp } from '../utils';
import { getPrincipal, AccessLevel, Principal } from 'src/auth';

export interface Props {
    children: (props: Scope) => React.ReactElement;
    attrName: string;
}

export type Scope = {
    attrName: string;
    attr: Attribute;
    onChange: (value: types.Value) => void;
    onLink: (ids: types.ID[]) => void;
    onUnLink: (ids: types.ID[]) => void;
    onDelete: (ids: types.ID[]) => void;
    value?: types.Value;
    errors?: types.Error[];
    field?: Field;
    vector?: RelPoint;
    relEntity?: types.Entity;
    relEntities?: types.Entity[];
    stack: types.StackItem[];
    userAccess: AccessLevel | null;
}

const AttributeProvider: React.FC<Props> = ({ children, attrName }) => {
    const dispatch = useDispatch();
    const principal: Principal = getPrincipal();
    const explorerScope: ExplorerScope = useContext(ExplorerContext)!;
    const { state, locator, entity, state: { stack } } = explorerScope;
    const attr: Attribute = conf.getAttribute(entity?.morph ?? locator.name, attrName)!;

    const { values, errors: allErrors } = entity ?? {};
    const value = _.get(values, attrName);
    const errors: types.Error[] | undefined = _.get(allErrors || {}, attrName, undefined);

    let field: Field | undefined = undefined;
    let vector: RelPoint | undefined = undefined;
    let relEntity: types.Entity | undefined = undefined;
    let relEntities: types.Entity[] | undefined = undefined;
    let userAccess: AccessLevel | null = AccessLevel.Manage;

    if (isRelationshipVector(attr)) {
        vector = attr as RelPoint;
        const { side: dir, rel } = vector;
        // const userCanRead: boolean = principal.hasAccessResource(dir === RelSide.SOURCE ? rel.related : rel.resource, AccessLevel.Read);
        // if (!userCanRead) {
        //     return null;
        // }
        userAccess = principal.getAccessLevel(dir === RelSide.SOURCE ? rel.related : rel.resource);

        if (value !== undefined) {
            if (dir === RelSide.SOURCE) {
                if (rel.metaType === RelType.MANY_TO_ONE || rel.metaType === RelType.ONE_TO_ONE) {
                    relEntity = _.get(state, ['entities', rel.related, value as types.ID]);
                }
                if (rel.metaType === RelType.MANY_TO_MANY) {
                    relEntities = (value as types.ID[]).map(value => _.get(state, ['entities', rel.related, value as types.ID]));
                }
            }
            if (dir === RelSide.TARGET) {
                if (rel.metaType === RelType.MANY_TO_ONE || rel.metaType === RelType.MANY_TO_MANY) {
                    relEntities = (value as types.ID[]).map(value => _.get(state, ['entities', rel.resource, value as types.ID]));
                }
                if (rel.metaType === RelType.ONE_TO_ONE) {
                    relEntity = _.get(state, ['entities', rel.resource, value as types.ID]);
                }
            }
        }
    } else {
        field = attr as Field;
    }

    const onChange: (value: types.Value) => void = (value) => {
        dispatch(actions.setFieldValAction(locator, field!, value));
    }

    const onLink: (ids: types.ID[]) => void = (ids) => {
        dispatch(actions.link(locator, vector!, ids));
    }

    const onUnLink: (ids: types.ID[]) => void = (ids) => {
        dispatch(actions.unlink(locator, vector!, ids));
    }

    const onDelete: (ids: types.ID[]) => void = (ids) => {
        dispatch(actions.deleteRelAction(locator, vector!, ids));
    }

    const scope = {
        attrName,
        attr,
        onChange,
        onLink,
        onUnLink,
        onDelete,
        value,
        errors,
        field,
        vector,
        relEntity,
        relEntities,
        stack,
        userAccess
    };

    return (
        children(scope)
    )
}

export default AttributeProvider;
